import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { researchAPI, e2eClonerAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus, Trash2, User, FileText, CheckCircle, Loader2, Clock,
  Video, Wand2, Folder, AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const styleStatusConfig = {
  pending: { label: 'Pending', color: 'bg-zinc-100 text-zinc-600', icon: Clock },
  analyzing: { label: 'Analyzing...', color: 'bg-blue-50 text-blue-700', icon: Loader2 },
  analyzed: { label: 'Ready', color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle },
};

const e2eStatusConfig = {
  pending: { label: 'No Analysis', color: 'bg-zinc-100 text-zinc-600', icon: Clock },
  analyzing: { label: 'Analyzing...', color: 'bg-blue-50 text-blue-700', icon: Loader2 },
  analyzed: { label: 'Analyzed', color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle },
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export default function Research() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('style');

  // Style Cloner state
  const [influencers, setInfluencers] = useState([]);
  const [loadingStyle, setLoadingStyle] = useState(true);
  const [showStyleDialog, setShowStyleDialog] = useState(false);
  const [styleForm, setStyleForm] = useState({ name: '', handle: '' });
  const [savingStyle, setSavingStyle] = useState(false);
  const [deleteStyleId, setDeleteStyleId] = useState(null);

  // E2E Cloner state
  const [projects, setProjects] = useState([]);
  const [loadingE2E, setLoadingE2E] = useState(true);
  const [showE2EDialog, setShowE2EDialog] = useState(false);
  const [e2eForm, setE2eForm] = useState({ name: '', notes: '' });
  const [savingE2E, setSavingE2E] = useState(false);
  const [deleteE2EId, setDeleteE2EId] = useState(null);

  const loadStyle = async () => {
    try {
      setLoadingStyle(true);
      const res = await researchAPI.listInfluencers();
      setInfluencers(res.data || []);
    } catch {
      toast.error('Failed to load Style Cloner influencers');
    } finally {
      setLoadingStyle(false);
    }
  };

  const loadE2E = async () => {
    try {
      setLoadingE2E(true);
      const res = await e2eClonerAPI.listProjects();
      setProjects(res.data || []);
    } catch {
      toast.error('Failed to load E2E Cloner projects');
    } finally {
      setLoadingE2E(false);
    }
  };

  useEffect(() => {
    loadStyle();
    loadE2E();
  }, []);

  const handleCreateStyle = async () => {
    if (!styleForm.name.trim()) { toast.error('Name is required'); return; }
    try {
      setSavingStyle(true);
      const res = await researchAPI.createInfluencer(styleForm);
      toast.success('Influencer added');
      setShowStyleDialog(false);
      setStyleForm({ name: '', handle: '' });
      navigate(`/research/influencers/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSavingStyle(false);
    }
  };

  const handleDeleteStyle = async (id) => {
    try {
      await researchAPI.deleteInfluencer(id);
      toast.success('Deleted');
      setDeleteStyleId(null);
      loadStyle();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleCreateE2E = async () => {
    if (!e2eForm.name.trim()) { toast.error('Project name is required'); return; }
    try {
      setSavingE2E(true);
      const res = await e2eClonerAPI.createProject(e2eForm);
      toast.success('Project created');
      setShowE2EDialog(false);
      setE2eForm({ name: '', notes: '' });
      navigate(`/research/e2e-cloner/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setSavingE2E(false);
    }
  };

  const handleDeleteE2E = async (id) => {
    try {
      await e2eClonerAPI.deleteProject(id);
      toast.success('Project deleted');
      setDeleteE2EId(null);
      loadE2E();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="research-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Research</h1>
        <p className="text-sm text-zinc-500 mt-1">Clone creator scripts at scale — by style, or word-for-word</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-200">
        <button
          onClick={() => setActiveTab('style')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 ${
            activeTab === 'style'
              ? 'text-zinc-900 border-zinc-900'
              : 'text-zinc-500 border-transparent hover:text-zinc-700'
          }`}
          data-testid="research-tab-style"
        >
          <span className="inline-flex items-center gap-1.5">
            <Wand2 size={15} /> Style Cloner
          </span>
        </button>
        <button
          onClick={() => setActiveTab('e2e')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 ${
            activeTab === 'e2e'
              ? 'text-zinc-900 border-zinc-900'
              : 'text-zinc-500 border-transparent hover:text-zinc-700'
          }`}
          data-testid="research-tab-e2e"
        >
          <span className="inline-flex items-center gap-1.5">
            <Video size={15} /> E2E Cloner
          </span>
        </button>
      </div>

      {/* ===== STYLE CLONER ===== */}
      {activeTab === 'style' && (
        <div data-testid="style-cloner-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-zinc-500" />
              <div>
                <h2 className="text-lg font-semibold text-zinc-800">Style Cloner</h2>
                <p className="text-xs text-zinc-500">
                  Analyze a creator's voice and rewrite any of your scripts in their style.
                </p>
              </div>
            </div>
            <Button
              onClick={() => { setStyleForm({ name: '', handle: '' }); setShowStyleDialog(true); }}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="add-influencer-btn"
            >
              <Plus size={16} className="mr-1.5" /> Add Influencer
            </Button>
          </div>

          {loadingStyle ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
            </div>
          ) : influencers.length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
              <User size={32} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-sm text-zinc-400">No influencers added yet</p>
              <p className="text-xs text-zinc-400 mt-1">Add an influencer to start analyzing their content style</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="influencer-grid">
              {influencers.map(inf => {
                const st = styleStatusConfig[inf.status] || styleStatusConfig.pending;
                const StatusIcon = st.icon;
                return (
                  <div
                    key={inf.id}
                    className="bg-white border border-zinc-200 rounded-lg p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => navigate(`/research/influencers/${inf.id}`)}
                    data-testid={`influencer-card-${inf.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-100 rounded-full flex items-center justify-center">
                          <User size={18} className="text-zinc-400" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-zinc-900">{inf.name}</h3>
                          {inf.handle && <p className="text-xs text-zinc-400">@{inf.handle}</p>}
                        </div>
                      </div>
                      <Badge className={`text-xs ${st.color}`}>
                        <StatusIcon size={12} className={`mr-1 ${inf.status === 'analyzing' ? 'animate-spin' : ''}`} />
                        {st.label}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-zinc-400">
                      <span>{inf.videos?.length || 0} videos</span>
                      <span>{inf.transcripts?.filter(t => !t.text?.startsWith('[')).length || 0} transcripts</span>
                      <span>{formatDate(inf.created_at)}</span>
                    </div>

                    {inf.style_analysis && (
                      <p className="text-xs text-zinc-500 line-clamp-2 border-t border-zinc-100 pt-2">
                        {inf.style_analysis.slice(0, 120)}...
                      </p>
                    )}

                    <div className="flex gap-2 mt-auto pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs text-zinc-700 border-zinc-200"
                        onClick={(e) => { e.stopPropagation(); navigate(`/research/influencers/${inf.id}`); }}
                        data-testid={`view-influencer-${inf.id}`}
                      >
                        {inf.status === 'analyzed' ? 'Convert Script' : 'View Details'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-400 hover:text-red-600"
                        onClick={(e) => { e.stopPropagation(); setDeleteStyleId(inf.id); }}
                        data-testid={`delete-influencer-${inf.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== E2E CLONER ===== */}
      {activeTab === 'e2e' && (
        <div data-testid="e2e-cloner-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Video size={18} className="text-zinc-500" />
              <div>
                <h2 className="text-lg font-semibold text-zinc-800">E2E Cloner</h2>
                <p className="text-xs text-zinc-500">
                  Drop a batch of winning creator videos. We transcribe each, keep the hook
                  word-for-word, and lightly rewrite the body — ready to shoot.
                </p>
              </div>
            </div>
            <Button
              onClick={() => { setE2eForm({ name: '', notes: '' }); setShowE2EDialog(true); }}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="add-e2e-project-btn"
            >
              <Plus size={16} className="mr-1.5" /> New Project
            </Button>
          </div>

          {loadingE2E ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
            </div>
          ) : projects.length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
              <Folder size={32} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-sm text-zinc-400">No projects yet</p>
              <p className="text-xs text-zinc-400 mt-1">
                Create a project, upload your batch of creator videos, and run analysis.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="e2e-project-grid">
              {projects.map(p => {
                const st = e2eStatusConfig[p.status] || e2eStatusConfig.pending;
                const StatusIcon = st.icon;
                return (
                  <div
                    key={p.id}
                    className="bg-white border border-zinc-200 rounded-lg p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => navigate(`/research/e2e-cloner/${p.id}`)}
                    data-testid={`e2e-project-card-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-violet-50 rounded-md flex items-center justify-center flex-shrink-0">
                          <Video size={18} className="text-violet-500" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-zinc-900 truncate">{p.name}</h3>
                          {p.notes && <p className="text-xs text-zinc-400 truncate">{p.notes}</p>}
                        </div>
                      </div>
                      <Badge className={`text-xs flex-shrink-0 ${st.color}`}>
                        <StatusIcon size={12} className={`mr-1 ${p.status === 'analyzing' ? 'animate-spin' : ''}`} />
                        {st.label}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-zinc-400">
                      <span>{p.video_count || 0} videos</span>
                      <span className="text-emerald-600">{p.analyzed_count || 0} analyzed</span>
                      {p.error_count > 0 && (
                        <span className="text-red-600 inline-flex items-center gap-1">
                          <AlertCircle size={11} /> {p.error_count} errors
                        </span>
                      )}
                      <span className="ml-auto">{formatDate(p.created_at)}</span>
                    </div>

                    <div className="flex gap-2 mt-auto pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs text-zinc-700 border-zinc-200"
                        onClick={(e) => { e.stopPropagation(); navigate(`/research/e2e-cloner/${p.id}`); }}
                        data-testid={`open-e2e-project-${p.id}`}
                      >
                        Open Project
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-400 hover:text-red-600"
                        onClick={(e) => { e.stopPropagation(); setDeleteE2EId(p.id); }}
                        data-testid={`delete-e2e-project-${p.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== Add Style Cloner Influencer Dialog ===== */}
      <Dialog open={showStyleDialog} onOpenChange={setShowStyleDialog}>
        <DialogContent className="sm:max-w-md bg-white text-zinc-900" data-testid="add-influencer-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900">Add Influencer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-700">Name</Label>
              <Input
                value={styleForm.name}
                onChange={e => setStyleForm({ ...styleForm, name: e.target.value })}
                placeholder="e.g. Tanmay Bhat"
                className="mt-1 bg-white border-zinc-200 text-zinc-900"
                data-testid="influencer-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-700">Instagram Handle (optional)</Label>
              <Input
                value={styleForm.handle}
                onChange={e => setStyleForm({ ...styleForm, handle: e.target.value })}
                placeholder="e.g. tanmaybhat"
                className="mt-1 bg-white border-zinc-200 text-zinc-900"
                data-testid="influencer-handle-input"
              />
            </div>
            <Button
              onClick={handleCreateStyle}
              disabled={savingStyle}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="create-influencer-btn"
            >
              {savingStyle ? 'Creating...' : 'Add & Upload Videos'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Delete Style Cloner Dialog ===== */}
      <Dialog open={!!deleteStyleId} onOpenChange={() => setDeleteStyleId(null)}>
        <DialogContent className="sm:max-w-sm bg-white text-zinc-900">
          <DialogHeader>
            <DialogTitle className="text-zinc-900">Delete this influencer?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">All videos, transcripts, and style analysis will be permanently deleted.</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1 border-zinc-200 text-zinc-700" onClick={() => setDeleteStyleId(null)}>Cancel</Button>
            <Button className="flex-1 bg-red-600 text-white hover:bg-red-700" onClick={() => handleDeleteStyle(deleteStyleId)}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Add E2E Project Dialog ===== */}
      <Dialog open={showE2EDialog} onOpenChange={setShowE2EDialog}>
        <DialogContent className="sm:max-w-md bg-white text-zinc-900" data-testid="add-e2e-project-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900">New E2E Cloner Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-700">Project Name</Label>
              <Input
                value={e2eForm.name}
                onChange={e => setE2eForm({ ...e2eForm, name: e.target.value })}
                placeholder="e.g. Aug Top 50 — Tanmay Bhat"
                className="mt-1 bg-white border-zinc-200 text-zinc-900"
                data-testid="e2e-project-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-700">Notes (optional)</Label>
              <Textarea
                value={e2eForm.notes}
                onChange={e => setE2eForm({ ...e2eForm, notes: e.target.value })}
                placeholder="Best 50 videos from last 120 days..."
                rows={3}
                className="mt-1 bg-white border-zinc-200 text-zinc-900 resize-none"
                data-testid="e2e-project-notes-input"
              />
            </div>
            <Button
              onClick={handleCreateE2E}
              disabled={savingE2E}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="create-e2e-project-btn"
            >
              {savingE2E ? 'Creating...' : 'Create & Upload Videos'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Delete E2E Project Dialog ===== */}
      <Dialog open={!!deleteE2EId} onOpenChange={() => setDeleteE2EId(null)}>
        <DialogContent className="sm:max-w-sm bg-white text-zinc-900">
          <DialogHeader>
            <DialogTitle className="text-zinc-900">Delete this project?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">All videos, transcripts, and rewritten scripts in this project will be permanently deleted.</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1 border-zinc-200 text-zinc-700" onClick={() => setDeleteE2EId(null)}>Cancel</Button>
            <Button className="flex-1 bg-red-600 text-white hover:bg-red-700" onClick={() => handleDeleteE2E(deleteE2EId)}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
